import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ShieldCheck, RefreshCw, Plus, Download, XCircle, Loader2,
  CheckCircle2, Clock, Ban, FileLock2, QrCode, Search, Server, IdCard,
} from "lucide-react";
import ConsentQrPosterModal from "@/components/ipd/ConsentQrPosterModal";
import ABDMPatientDiscoveryModal from "@/components/ipd/ABDMPatientDiscoveryModal";
import ABDMGatewayInboxModal from "@/components/ipd/ABDMGatewayInboxModal";
import ABDMABHARegistrationModal from "@/components/ipd/ABDMABHARegistrationModal";
import { useAuth } from "@/contexts/AuthContext";

/**
 * ABDMConsentsPanel — receptionist view for ABDM (Ayushman Bharat) consent
 * lifecycle. Talks to the M2 backend router:
 *   POST /abdm/consent/init                     (send OTP)
 *   POST /abdm/consent/otp/verify               (approve)
 *   PUT  /abdm/consent/{id}/reject              (deny)
 *   PUT  /abdm/consent/{id}/revoke              (revoke after approval)
 *   GET  /abdm/consents?status=…                (list)
 *   GET  /abdm/consent/{id}/status              (poll)
 *   GET  /abdm/audit-log                        (audit trail)
 *
 * Sandbox mode returns `demo_otp="123456"` in the init response so the
 * receptionist can immediately verify without waiting for a real ABHA push.
 * Approved consents can be downloaded as a signed artefact JSON.
 */

const DOC_OPTIONS = [
  { id: "ipd_admission", label: "Admission" },
  { id: "billing",       label: "Billing" },
  { id: "discharge",     label: "Discharge" },
  { id: "prescription",  label: "Prescription" },
  { id: "lab",           label: "Lab reports" },
  { id: "imaging",       label: "Imaging" },
  { id: "opd_note",      label: "OPD notes" },
];

const PURPOSE_OPTIONS = [
  { code: "CAREMGT",  label: "Care Management" },
  { code: "BTG",      label: "Break-the-Glass (emergency)" },
  { code: "PUBHLTH",  label: "Public Health" },
  { code: "HPAYMT",   label: "Healthcare Payment" },
  { code: "DSRCH",    label: "Disease-specific Research" },
  { code: "PATRQT",   label: "Patient Requested" },
];

function StatusBadge({ status }) {
  const map = {
    requested:  { label: "Requested", cls: "bg-slate-100 text-slate-700",     Icon: Clock },
    otp_sent:   { label: "OTP sent",  cls: "bg-amber-100 text-amber-800",     Icon: Clock },
    approved:   { label: "Approved",  cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
    rejected:   { label: "Rejected",  cls: "bg-rose-100 text-rose-700",       Icon: XCircle },
    revoked:    { label: "Revoked",   cls: "bg-rose-100 text-rose-700",       Icon: Ban },
    expired:    { label: "Expired",   cls: "bg-slate-100 text-slate-500",     Icon: Clock },
  };
  const cfg = map[status] || { label: status, cls: "bg-slate-100 text-slate-700", Icon: Clock };
  const Icon = cfg.Icon;
  return (
    <Badge className={`${cfg.cls} border-transparent hover:${cfg.cls}`}
           data-testid={`abdm-status-${status}`}>
      <Icon size={12} className="mr-1" /> {cfg.label}
    </Badge>
  );
}

function DocumentPills({ docs }) {
  if (!docs || docs.length === 0)
    return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {docs.slice(0, 4).map((d) => (
        <span key={d}
              className="px-1.5 py-0.5 text-[10px] rounded bg-blue-50 text-blue-700 border border-blue-200">
          {DOC_OPTIONS.find((o) => o.id === d)?.label || d}
        </span>
      ))}
      {docs.length > 4 && (
        <span className="px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-500">
          +{docs.length - 4}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Consent request modal
// ---------------------------------------------------------------------------
function RequestConsentModal({ open, onClose, patients, onCreated, onQrPoster }) {
  const [patientId, setPatientId] = useState("");
  const [purpose, setPurpose] = useState("Care management for IPD stay");
  const [purposeCode, setPurposeCode] = useState("CAREMGT");
  const [docs, setDocs] = useState(["ipd_admission", "billing"]);
  const [busy, setBusy] = useState(false);
  const [initResult, setInitResult] = useState(null);
  const [otp, setOtp] = useState("");

  useEffect(() => {
    if (!open) {
      setPatientId(""); setPurpose("Care management for IPD stay");
      setPurposeCode("CAREMGT"); setDocs(["ipd_admission", "billing"]);
      setInitResult(null); setOtp("");
    }
  }, [open]);

  const toggleDoc = (id) =>
    setDocs((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!patientId) return toast.error("Select a patient first");
    if (docs.length === 0) return toast.error("Pick at least one document type");
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/consent/init", {
        patient_id: patientId,
        purpose,
        purpose_code: purposeCode,
        document_types: docs,
      });
      setInitResult(data);
      toast.success("Consent request sent — patient has 10 minutes to approve");
      if (data.demo_otp) setOtp(data.demo_otp);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send consent");
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    if (!initResult?.id) return;
    setBusy(true);
    try {
      await api.post("/abdm/consent/otp/verify", {
        consent_id: initResult.id,
        otp,
      });
      toast.success("Consent approved and signed");
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OTP verification failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="abdm-request-consent-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-emerald-600" />
            Request ABHA Consent
          </DialogTitle>
        </DialogHeader>

        {!initResult ? (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs">Patient *</Label>
              <Select value={patientId} onValueChange={setPatientId}>
                <SelectTrigger data-testid="abdm-consent-patient">
                  <SelectValue placeholder="Select an admitted patient" />
                </SelectTrigger>
                <SelectContent>
                  {patients.length === 0 ? (
                    <SelectItem value="__none__" disabled>No active admissions</SelectItem>
                  ) : patients.map((p) => (
                    <SelectItem key={p.id} value={p.patient_id || p.id}>
                      {p.patient_name || p.name}
                      {p.patient_mobile ? ` · ${p.patient_mobile}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Purpose *</Label>
              <Select value={purposeCode} onValueChange={setPurposeCode}>
                <SelectTrigger data-testid="abdm-consent-purpose">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PURPOSE_OPTIONS.map((p) => (
                    <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea value={purpose} onChange={(e) => setPurpose(e.target.value)}
                        rows={2} className="mt-2 text-xs"
                        placeholder="Free-text purpose shown to the patient"
                        data-testid="abdm-consent-purpose-text" />
            </div>

            <div>
              <Label className="text-xs">Documents to share *</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1">
                {DOC_OPTIONS.map((d) => {
                  const on = docs.includes(d.id);
                  return (
                    <button type="button" key={d.id} onClick={() => toggleDoc(d.id)}
                            data-testid={`abdm-consent-doc-${d.id}`}
                            className={`text-xs px-2 py-1.5 rounded border text-left ${
                              on
                                ? "bg-blue-50 border-blue-300 text-blue-800"
                                : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                            }`}>
                      {on ? "✓" : "◯"} {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="abdm-consent-submit">
                {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <ShieldCheck size={14} className="mr-1" />}
                Send consent request
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-3" data-testid="abdm-consent-otp-panel">
            <Card className="p-3 border-emerald-200 bg-emerald-50/60">
              <p className="text-xs uppercase tracking-wide text-emerald-800">
                Consent request created
              </p>
              <p className="text-sm text-slate-700 mt-1">
                Consent ID: <span className="font-mono">{initResult.id.slice(0, 8)}…</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Expires: {new Date(initResult.expires_at).toLocaleString("en-IN")}
              </p>
            </Card>
            <div>
              <Label className="text-xs">
                Enter the 6-digit OTP the patient received
                {initResult.demo_otp && (
                  <span className="ml-2 text-emerald-700">
                    (sandbox demo OTP: <span className="font-mono">{initResult.demo_otp}</span>)
                  </span>
                )}
              </Label>
              <Input value={otp} onChange={(e) => setOtp(e.target.value)}
                     inputMode="numeric" maxLength={6} className="mt-1 tracking-widest"
                     data-testid="abdm-consent-otp-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Close &amp; poll later
              </Button>
              <Button type="button" variant="outline"
                      onClick={() => { onQrPoster?.(initResult); }}
                      className="text-emerald-700 border-emerald-200"
                      data-testid="abdm-consent-open-qr">
                Print QR poster
              </Button>
              <Button onClick={verifyOtp} disabled={busy || otp.length < 4}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="abdm-consent-verify-otp">
                {busy ? <Loader2 className="animate-spin mr-1" size={14} /> : <CheckCircle2 size={14} className="mr-1" />}
                Verify &amp; Approve
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------
export default function ABDMConsentsPanel() {
  const { user } = useAuth();
  const hospitalName = user?.org_name || user?.hospital_name || "Hospital";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [patients, setPatients] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [qrConsent, setQrConsent] = useState(null);
  const [showDiscovery, setShowDiscovery] = useState(false);
  const [showGateway, setShowGateway] = useState(false);
  const [showAbha, setShowAbha] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter === "all" ? "" : `?status=${statusFilter}`;
      const { data } = await api.get(`/abdm/consents${q}`);
      setRows(data.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load consents");
    } finally { setLoading(false); }
  }, [statusFilter]);

  const loadPatients = useCallback(async () => {
    try {
      const { data } = await api.get("/ipd/admissions?status=active");
      setPatients(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPatients(); }, [loadPatients]);

  // Poll pending consents every 30 s so approvals from the ABHA app show up.
  useEffect(() => {
    const pendingCount = rows.filter((r) =>
      ["requested", "otp_sent"].includes(r.status)).length;
    if (pendingCount === 0) return undefined;
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [rows, load]);

  const revoke = async (row) => {
    if (!window.confirm(`Revoke consent for ${row.patient_name}? This cannot be undone.`)) return;
    setBusyId(row.id);
    try {
      await api.put(`/abdm/consent/${row.id}/revoke`);
      toast.success("Consent revoked");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    } finally { setBusyId(""); }
  };

  const reject = async (row) => {
    const reason = window.prompt("Reason for rejection?", "Patient declined");
    if (!reason || reason.trim().length < 3) return;
    setBusyId(row.id);
    try {
      await api.put(`/abdm/consent/${row.id}/reject`, { reason });
      toast.success("Consent rejected");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    } finally { setBusyId(""); }
  };

  const downloadArtefact = (row) => {
    // Build a client-side JSON blob with everything a receiving system
    // would need to verify the consent: canonical body, signature, artefact
    // ciphertext. Nothing sensitive leaves the browser.
    const payload = {
      consent_id: row.id,
      patient_id: row.patient_id,
      patient_name: row.patient_name,
      hospital_id: row.hospital_id,
      purpose: row.purpose,
      purpose_code: row.purpose_code,
      document_types: row.document_types,
      status: row.status,
      requested_at: row.requested_at,
      approved_at: row.approved_at,
      expires_at: row.expires_at,
      canonical: row.canonical,
      signature: row.artefact_signature,
      artefact: row.artefact,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = (row.approved_at || row.requested_at || "").slice(0, 10);
    a.href = url;
    a.download = `consent_${(row.patient_name || "patient").replace(/\s+/g, "_")}_${stamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Signed artefact downloaded");
  };

  const summary = useMemo(() => {
    const s = { total: rows.length, approved: 0, otp_sent: 0, revoked: 0, rejected: 0 };
    rows.forEach((r) => { if (s[r.status] !== undefined) s[r.status] += 1; });
    return s;
  }, [rows]);

  return (
    <div className="space-y-3" data-testid="ipd-tab-abdm">
      <div className="flex justify-between items-center gap-2 flex-wrap">
        <div className="flex items-center gap-3 text-xs text-slate-600">
          <span className="inline-flex items-center gap-1">
            <FileLock2 size={14} className="text-emerald-600" />
            <strong>{summary.total}</strong> consents
          </span>
          <span className="text-emerald-700"><strong>{summary.approved}</strong> approved</span>
          <span className="text-amber-700"><strong>{summary.otp_sent}</strong> awaiting OTP</span>
          <span className="text-rose-700">
            <strong>{summary.revoked + summary.rejected}</strong> rejected/revoked
          </span>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="abdm-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="otp_sent">Awaiting OTP</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="revoked">Revoked</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={load}
                  data-testid="abdm-refresh-btn">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowAbha(true)}
                  className="text-blue-700 border-blue-200"
                  data-testid="abdm-abha-registration-btn">
            <IdCard size={14} className="mr-1" /> Create ABHA
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowDiscovery(true)}
                  className="text-blue-700 border-blue-200"
                  data-testid="abdm-discovery-btn">
            <Search size={14} className="mr-1" /> Find patient
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowGateway(true)}
                  className="text-emerald-700 border-emerald-200"
                  data-testid="abdm-gateway-btn">
            <Server size={14} className="mr-1" /> Gateway
          </Button>
          <Button size="sm" onClick={() => setShowModal(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="abdm-request-consent-btn">
            <Plus size={14} className="mr-1" /> Request Consent
          </Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Patient</TableHead>
            <TableHead>Purpose</TableHead>
            <TableHead>Documents</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Requested</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">
              <Loader2 className="animate-spin mx-auto mb-1" size={18} />
              Loading consents…
            </TableCell></TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-slate-400 py-8">
                No consents yet — request one to get started.
              </TableCell>
            </TableRow>
          ) : rows.map((r) => (
            <TableRow key={r.id} data-testid={`abdm-consent-row-${r.id}`}>
              <TableCell className="font-medium">
                {r.patient_name || <span className="text-slate-400">—</span>}
                {r.patient_mobile && (
                  <span className="block text-xs text-slate-500">{r.patient_mobile}</span>
                )}
              </TableCell>
              <TableCell>
                <span className="text-xs">{r.purpose_code}</span>
                <span className="block text-[10px] text-slate-500 max-w-[16ch] truncate">
                  {r.purpose}
                </span>
              </TableCell>
              <TableCell><DocumentPills docs={r.document_types} /></TableCell>
              <TableCell><StatusBadge status={r.status} /></TableCell>
              <TableCell className="text-xs">
                {new Date(r.requested_at).toLocaleDateString("en-IN", {
                  day: "numeric", month: "short",
                })}
                <span className="block text-[10px] text-slate-500">
                  {new Date(r.requested_at).toLocaleTimeString("en-IN", {
                    hour: "2-digit", minute: "2-digit",
                  })}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  {(r.status === "otp_sent" || r.status === "requested") && (
                    <Button size="sm" variant="outline" onClick={() => setQrConsent(r)}
                            className="text-emerald-700 border-emerald-200"
                            data-testid={`abdm-qr-${r.id}`}>
                      <QrCode size={12} className="mr-1" /> QR Poster
                    </Button>
                  )}
                  {r.status === "otp_sent" && (
                    <Button size="sm" variant="outline" onClick={() => reject(r)}
                            disabled={busyId === r.id}
                            className="text-rose-700 border-rose-200"
                            data-testid={`abdm-reject-${r.id}`}>
                      <XCircle size={12} className="mr-1" /> Reject
                    </Button>
                  )}
                  {r.status === "approved" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => downloadArtefact(r)}
                              className="text-blue-700 border-blue-200"
                              data-testid={`abdm-download-${r.id}`}>
                        <Download size={12} className="mr-1" /> Artefact
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => revoke(r)}
                              disabled={busyId === r.id}
                              className="text-rose-700 border-rose-200"
                              data-testid={`abdm-revoke-${r.id}`}>
                        <Ban size={12} className="mr-1" /> Revoke
                      </Button>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <RequestConsentModal
        open={showModal}
        onClose={() => setShowModal(false)}
        patients={patients}
        onCreated={load}
        onQrPoster={(newConsent) => setQrConsent(newConsent)}
      />
      <ConsentQrPosterModal
        open={!!qrConsent}
        onClose={() => setQrConsent(null)}
        consent={qrConsent}
        hospitalName={hospitalName}
      />
      <ABDMPatientDiscoveryModal
        open={showDiscovery}
        onClose={() => setShowDiscovery(false)}
        onLinked={() => { loadPatients(); load(); }}
      />
      <ABDMGatewayInboxModal
        open={showGateway}
        onClose={() => setShowGateway(false)}
      />
      <ABDMABHARegistrationModal
        open={showAbha}
        onClose={() => setShowAbha(false)}
        onRefresh={() => { setShowAbha(false); loadPatients(); }}
      />
    </div>
  );
}
